/**
 * Generic object pool with O(1) acquire/release using intrusive linked-list.
 * Objects are tracked via userData._poolNext pointer, avoiding array operations.
 */
export class ObjectPool {
    /**
     * @param {Object} config
     * @param {string} config.typeId - Unique identifier for this pool type
     * @param {Function} config.factory - Function that creates a new object
     * @param {Function} config.reset - Function that resets an object for reuse
     * @param {number} [config.maxSize=100] - Maximum pool size
     */
    constructor({ typeId, factory, reset, maxSize = 100 }) {
        this.typeId = typeId;
        this.factory = factory;
        this.reset = reset;
        this.maxSize = maxSize;

        // Linked list heads
        this._freeHead = null;   // Head of free list
        this._activeHead = null; // Head of active list (for iteration/releaseAll)

        // Counts for stats
        this._freeCount = 0;
        this._activeCount = 0;
        this._totalCreated = 0;
    }

    /**
     * Pre-populate pool with objects.
     * @param {number} count - Number of objects to create
     */
    warmUp(count) {
        const toCreate = Math.min(count, this.maxSize - this._totalCreated);
        for (let i = 0; i < toCreate; i++) {
            const obj = this._createObject();
            this._addToFreeList(obj);
        }
    }

    /**
     * Acquire an object from the pool. O(1) operation.
     * @param {Object} [options] - Options passed to reset function
     * @returns {Object|null} - The acquired object, or null if pool is exhausted
     */
    acquire(options = {}) {
        let obj;

        if (this._freeHead) {
            // Pop from free list
            obj = this._freeHead;
            this._freeHead = obj.userData._poolNext;
            this._freeCount--;
        } else if (this._totalCreated < this.maxSize) {
            // Create new if under limit
            obj = this._createObject();
        } else {
            // Pool exhausted
            return null;
        }

        // Reset for reuse
        this.reset(obj, options);

        // Add to active list
        obj.userData._poolNext = this._activeHead;
        this._activeHead = obj;
        this._activeCount++;

        obj.userData._poolActive = true;
        return obj;
    }

    /**
     * Release an object back to the pool. O(1) removal from active, O(1) add to free.
     * Note: This uses lazy removal from active list - object stays in list but marked inactive.
     * @param {Object} obj - The object to release
     */
    release(obj) {
        if (!obj.userData._poolActive) {
            // Already released
            return;
        }

        obj.userData._poolActive = false;
        this._activeCount--;

        // Add to free list head
        this._addToFreeList(obj);
    }

    /**
     * Release all active objects back to the pool.
     */
    releaseAll() {
        let current = this._activeHead;
        while (current) {
            const next = current.userData._poolNext;
            if (current.userData._poolActive) {
                current.userData._poolActive = false;
                this._addToFreeList(current);
            }
            current = next;
        }
        this._activeHead = null;
        this._activeCount = 0;
    }

    /**
     * Iterate over all active objects.
     * @param {Function} callback - Called with each active object
     */
    forEachActive(callback) {
        let current = this._activeHead;
        let prev = null;

        // Clean up inactive nodes as we iterate (lazy cleanup)
        while (current) {
            const next = current.userData._poolNext;

            if (current.userData._poolActive) {
                callback(current);
                prev = current;
            } else {
                // Remove inactive node from active list
                if (prev) {
                    prev.userData._poolNext = next;
                } else {
                    this._activeHead = next;
                }
            }

            current = next;
        }
    }

    /**
     * Get count of active objects.
     * @returns {number}
     */
    getActiveCount() {
        return this._activeCount;
    }

    /**
     * Get count of free objects.
     * @returns {number}
     */
    getFreeCount() {
        return this._freeCount;
    }

    /**
     * Get total objects created by this pool.
     * @returns {number}
     */
    getTotalCreated() {
        return this._totalCreated;
    }

    /**
     * Dispose all objects in the pool.
     */
    dispose() {
        // Guard against double-dispose
        if (this._disposed) return;
        this._disposed = true;

        // Safety limit to prevent infinite loops from corrupted linked lists
        const maxIterations = Math.max(10000, this._activeCount + this._freeCount + 1000);
        let iterations = 0;

        // Dispose active objects
        let current = this._activeHead;
        while (current && iterations < maxIterations) {
            iterations++;
            const next = current.userData?._poolNext;
            this._disposeObject(current);
            if (current.userData) {
                current.userData._poolNext = null;
            }
            current = next;
        }

        // Dispose free objects
        current = this._freeHead;
        while (current && iterations < maxIterations) {
            iterations++;
            const next = current.userData?._poolNext;
            this._disposeObject(current);
            if (current.userData) {
                current.userData._poolNext = null;
            }
            current = next;
        }

        this._activeHead = null;
        this._freeHead = null;
        this._activeCount = 0;
        this._freeCount = 0;
        this._totalCreated = 0;
    }

    /**
     * Create a new object and tag it with pool metadata.
     * @private
     */
    _createObject() {
        const obj = this.factory();

        // Ensure userData exists
        if (!obj.userData) {
            obj.userData = {};
        }

        // Tag with pool info
        obj.userData._poolTypeId = this.typeId;
        obj.userData._poolNext = null;
        obj.userData._poolActive = false;

        this._totalCreated++;
        return obj;
    }

    /**
     * Add object to free list.
     * @private
     */
    _addToFreeList(obj) {
        obj.userData._poolNext = this._freeHead;
        this._freeHead = obj;
        this._freeCount++;
    }

    /**
     * Dispose a single object.
     * @private
     */
    _disposeObject(obj) {
        // Handle Three.js objects
        if (obj.traverse) {
            obj.traverse((child) => {
                if (child.geometry) {
                    child.geometry.dispose();
                }
                // Don't dispose shared materials - they're managed by ObjectFactory
            });
        } else if (obj.geometry) {
            obj.geometry.dispose();
        }
    }
}
